import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Typography, useTheme } from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import { useTranslation } from 'react-i18next'
import { HERO_GRADIENT, gradientCss } from '../../lib/kidColors'

const TABS = [
  { labelKey: 'kids.nav.learn', icon: HomeIcon, path: '/' },
  { labelKey: 'kids.nav.play', icon: SportsEsportsIcon, path: '/games' },
] as const

interface KidBottomNavProps {
  audioPlayerHeight?: number
}

export function KidBottomNav({ audioPlayerHeight = 0 }: KidBottomNavProps) {
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const activeIndex = TABS.findIndex((tab) =>
    tab.path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(tab.path),
  )
  const active = activeIndex >= 0 ? activeIndex : 0

  return (
    <Box
      component="nav"
      aria-label={t('kids.nav.label')}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar + 1,
        pb: 'env(safe-area-inset-bottom)',
        transform: `translateY(-${audioPlayerHeight}px)`,
        transition: 'transform 0.2s ease',
        px: 2,
        pt: 1,
        background: (th) =>
          th.palette.mode === 'light'
            ? 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.92) 24%)'
            : 'linear-gradient(180deg, transparent 0%, rgba(11,15,20,0.95) 24%)',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          maxWidth: 420,
          mx: 'auto',
          p: 1,
          borderRadius: '26px',
          bgcolor: 'background.paper',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.08)',
          border: 1,
          borderColor: 'divider',
        }}
      >
        {TABS.map((tab, index) => {
          const Icon = tab.icon
          const isActive = index === active
          return (
            <Box
              key={tab.path}
              component="button"
              type="button"
              onClick={() => navigate(tab.path)}
              aria-current={isActive ? 'page' : undefined}
              sx={{
                border: 'none',
                cursor: 'pointer',
                borderRadius: '18px',
                py: 1.25,
                px: 1,
                minHeight: 64,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                background: isActive ? gradientCss(HERO_GRADIENT, 135) : 'transparent',
                color: isActive ? 'common.white' : 'text.secondary',
                boxShadow: isActive ? `0 6px 18px ${HERO_GRADIENT[1]}55` : 'none',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:active': { transform: 'scale(0.97)' },
              }}
            >
              <Icon sx={{ fontSize: 28 }} aria-hidden />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                  lineHeight: 1.1,
                }}
              >
                {t(tab.labelKey)}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

